import type { PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useMemo } from "react";
import { useStatsigClient } from "@statsig/react-bindings";

import { STATSIG_EXPERIMENT } from "@/services/experiments/constants";

function useGetGroupSymbol(experimentName: string) {
  const statsigClient = useStatsigClient();

  const getGroupSymbol = useCallback(() => {
    const experiment = statsigClient.getExperiment(experimentName);
    return experiment.get("groupSymbol");
  }, [statsigClient]);

  return getGroupSymbol;
}

export interface FunnelExperimentContextValue {
  feedbackButtonsShown(): boolean;
  isCMNEnabled(): boolean;
  showAdditionalFianceSteps(): boolean;
  showNewQuestionsAndButtons(): boolean;
}

const defaultValue = {
  isCMNEnabled: () => {
    return false;
  },
  feedbackButtonsShown: () => {
    return false;
  },
  showAdditionalFianceSteps: () => {
    return false;
  },
  showNewQuestionsAndButtons: () => {
    return false;
  },
};

export const FunnelExperimentContext =
  createContext<FunnelExperimentContextValue>(defaultValue);

export function useFunnelExperimentContext() {
  return useContext(FunnelExperimentContext);
}

export function NoPraticeSelectedFunnelExperimentProvider(
  props: PropsWithChildren,
) {
  return (
    <FunnelExperimentContext.Provider value={defaultValue}>
      {props.children}
    </FunnelExperimentContext.Provider>
  );
}

export function FamilyFunnelExperimentProvider(props: PropsWithChildren) {
  const getGroupSymbol = useGetGroupSymbol(
    STATSIG_EXPERIMENT.funnel_test_questions_buttons_feedback,
  );

  const value = useMemo(() => {
    return {
      isCMNEnabled() {
        return true;
      },
      showAdditionalFianceSteps() {
        return false;
      },
      feedbackButtonsShown() {
        const feedbackComponentExperimentEnabled =
          getGroupSymbol() === "feedback_component";
        return feedbackComponentExperimentEnabled;
      },
      showNewQuestionsAndButtons() {
        const newQuestionsAndButtonsExperimentEnabled =
          getGroupSymbol() === "new_questions_and_buttons";
        return newQuestionsAndButtonsExperimentEnabled;
      },
    };
  }, [getGroupSymbol]);

  return (
    <FunnelExperimentContext.Provider value={value}>
      {props.children}
    </FunnelExperimentContext.Provider>
  );
}

export function ImmigrantFunnelExperimentProvider(props: PropsWithChildren) {
  const getGroupSymbol = useGetGroupSymbol(
    STATSIG_EXPERIMENT.imm_funnel_fiance_visa_steps_and_feedback_2025_04_28,
  );

  const getImmigrationCMNGroupSymbol = useGetGroupSymbol(
    STATSIG_EXPERIMENT.IMMIGRATION_CMN,
  );

  const value = useMemo(() => {
    return {
      isCMNEnabled() {
        return getImmigrationCMNGroupSymbol() === "enabled";
      },
      showAdditionalFianceSteps() {
        return getGroupSymbol() === "enabled";
      },
      feedbackButtonsShown() {
        return getGroupSymbol() === "enabled";
      },
      showNewQuestionsAndButtons() {
        return false;
      },
    };
  }, [getGroupSymbol]);

  return (
    <FunnelExperimentContext.Provider value={value}>
      {props.children}
    </FunnelExperimentContext.Provider>
  );
}
